import React, { forwardRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import './TimeSeriesChart.css';
import { applyOutlierFilter } from '../utils/outlierFilter';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    Filler
);

// Curated color palette for dark backgrounds
const CHART_COLORS = [
    { border: '#638cff', bg: 'rgba(99, 140, 255, 0.1)' },
    { border: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' },
    { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
    { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
    { border: '#a855f7', bg: 'rgba(168, 85, 247, 0.1)' },
    { border: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)' },
];

/**
 * TimeSeriesChart — Reusable line chart for BMS parameter data
 *
 * Props:
 *   data           — historicData array (each row has { timestamp, [paramKey]: { value, unit } })
 *   parameters     — array of { key, name } for which lines to plot
 *   title          — chart heading string
 *   yAxisLabel     — optional Y-axis label
 *   filterOutliers — optional boolean to filter out erroneous data spikes
 *   filterMode     — optional string 'off'|'light'|'aggressive' for outlier filtering
 */
const TimeSeriesChart = forwardRef(function TimeSeriesChart({ data, parameters, title, yAxisLabel, filterOutliers = false, filterMode }, ref) {
    if (!data || data.length === 0 || !parameters || parameters.length === 0) {
        return null;
    }

    // Sort data by timestamp ascending
    const sorted = [...data].sort((a, b) => a.timestamp - b.timestamp);

    // New callers pass filterMode; legacy callers (RBMS) pass the filterOutliers
    // boolean, which maps to 'light' (physical clamp + rate-of-change gate).
    const mode = filterMode || (filterOutliers ? 'light' : 'off');

    // Build labels (timestamps)
    const labels = sorted.map(row => new Date(row.timestamp));

    // Build datasets
    const datasets = parameters.map((param, idx) => {
        const color = CHART_COLORS[idx % CHART_COLORS.length];
        const rawValues = sorted.map(row => {
            const val = row[param.key]?.value;
            if (val === undefined || val === null || val === 'N/A') return null;
            const num = parseFloat(val);
            return isNaN(num) ? null : num;
        });
        return {
            label: param.name,
            data: applyOutlierFilter(rawValues, param.key, mode),
            borderColor: color.border,
            backgroundColor: color.bg,
            borderWidth: 2,
            pointRadius: 1.5,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: parameters.length === 1,
            spanGaps: true,
        };
    });

    const chartData = { labels, datasets };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: '#8892b0',
                    font: { size: 12, weight: 500 },
                    padding: 16,
                    usePointStyle: true,
                    pointStyle: 'circle',
                },
            },
            title: {
                display: !!title,
                text: title,
                color: '#c8d1e0',
                font: { size: 14, weight: 600 },
                padding: { bottom: 12 },
            },
            tooltip: {
                backgroundColor: 'rgba(16, 22, 36, 0.95)',
                titleColor: '#c8d1e0',
                bodyColor: '#8892b0',
                borderColor: 'rgba(99, 140, 255, 0.2)',
                borderWidth: 1,
                padding: 10,
                cornerRadius: 8,
                titleFont: { size: 12 },
                bodyFont: { size: 12 },
                callbacks: {
                    title: (tooltipItems) => {
                        const date = new Date(tooltipItems[0].parsed.x);
                        return date.toLocaleString('en-US', {
                            month: 'short', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', second: '2-digit',
                            hour12: false,
                        });
                    },
                },
            },
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    tooltipFormat: 'PPpp',
                    displayFormats: {
                        minute: 'HH:mm',
                        hour: 'HH:mm',
                        day: 'MMM dd',
                    },
                },
                ticks: {
                    color: '#5a6687',
                    font: { size: 11 },
                    maxTicksLimit: 12,
                    maxRotation: 0,
                },
                grid: {
                    color: 'rgba(99, 140, 255, 0.05)',
                },
            },
            y: {
                title: {
                    display: !!yAxisLabel,
                    text: yAxisLabel || '',
                    color: '#5a6687',
                    font: { size: 11 },
                },
                ticks: {
                    color: '#5a6687',
                    font: { size: 11 },
                },
                grid: {
                    color: 'rgba(99, 140, 255, 0.05)',
                },
            },
        },
    };

    return (
        <div className="chart-container">
            <Line ref={ref} data={chartData} options={options} />
        </div>
    );
});

export default TimeSeriesChart;
